import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import isEmailProper from '../../../utils/isEmailProper';
import isEmpty from 'validator/lib/isEmpty';
import isEmail from 'validator/lib/isEmail';
import classNames from 'classnames';

import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import HelpRoundedIcon from '@material-ui/icons/HelpRounded';

import GDPRHint from '../GDPRHint';
import { FormattedMessageWrappedInSpan } from '../../misc';

////////////////////////////////////////////
// TODO MISSING TESTS
////////////////////////////////////////////

const ENTER_KEY = 'Enter';

const translations = defineMessages({
  emailTextFieldPlaceholder: {
    id: 'requestRecheckView.emailTextField.placeholder',
    defaultMessage: 'Enter your email address',
  },
  emailTextFieldLabel: {
    id: 'requestRecheckView.emailTextField.label',
    defaultMessage: 'Email',
  },
  informationAboutUsageMainSearch: {
    id: 'requestRecheckView.informationAboutUsage.tooltipMainSearch',
    defaultMessage: `This function is intended for customers who, for example, search for more photos of themselves using a friend's bib number.`,
  },
  informationAboutUsageNotMainSearch: {
    id: 'requestRecheckView.informationAboutUsage.tooltipNotMainSearch',
    defaultMessage: `We will recheck the photo. You will be notified by email and the photo will be added to your photo flat for free. Even after you have already purchased it.`,
  },
});

const RequestRecheckView = ({
  intl,
  isStartNumberInputDisabled,
  onCancel,
  lastUserInputStartNumber,
  ...props
}) => {
  const isNotOnMainSearch = () => {
    let location = useLocation();
    return /dual|category|gps|smartsearch/.test(location.pathname);
  };
  const notMainSearch = isNotOnMainSearch();
  const searchNumber = useParams().startnumber;

  const [email, setEmail] = useState(props.email.trim() || '');
  const [emailIsInvalid, setEmailIsInvalid] = useState(false);
  const [startNumber, setStartNumber] = useState(
    notMainSearch && lastUserInputStartNumber ? lastUserInputStartNumber
        : props.startnumber ? props.startnumber : searchNumber
  );
  const [startNumberIsInvalid, setStartNumberIsInvalid] = useState(false);

  const handleStartnumberChanged = (event) => {
    setStartNumber(event.target.value.replace(/ /gi, ''));
    if (!notMainSearch && event.target.value == props.startnumber) {
      setStartNumberIsInvalid(true);
    } else if (!isEmpty(event.target.value)) {
      setStartNumberIsInvalid(false);
    } else {
      setStartNumberIsInvalid(true);
    }
  };

  const handleEmailChanged = (event) => {
    setEmail(event.target.value.trim());
    if (isEmailProper(event.target.value)) {
      setEmailIsInvalid(false);
    } else {
      setEmailIsInvalid(true);
    }
  };

  const handleKeyPressed = (event, value) => {
    if (event.key === ENTER_KEY) {
      submitForm();
    }
  };

  const validateInput = () => {
    // For a few weeks now (as of september 4th 2019), users have been increasingly
    // entering their e-mail address instead of their starting number. Since the causes
    // for the problem are not fully understandable, I check if there is an e-mail address
    // entered in the start number field.

    if (
      startNumber === '' ||
      (startNumber &&
        isEmail(startNumber.trim(), {
          allow_utf8_local_part: false,
        }))
    ) {
      setStartNumberIsInvalid(true);
      return false;
    }

    if (!isEmailProper(email)) {
      setEmailIsInvalid(true);
      return false;
    }
    return true;
  };

  const submitForm = () => {
    if (validateInput()) {
      props.onSubmit(startNumber, email.trim());
      if (notMainSearch) {
        props.setLastUserInputStartNumber(startNumber);
      }
    }
  };

  return (
    <div className="notification-container">
      <div className="notification-body">
        {!isStartNumberInputDisabled && (
          <Tooltip
            title={
              notMainSearch
                ? intl.formatMessage(translations.informationAboutUsageNotMainSearch)
                : intl.formatMessage(translations.informationAboutUsageMainSearch)
            }
          >
            <h1 className="notification-title-big-margin-bottom ">
              <FormattedMessageWrappedInSpan
                id="requestRecheckView.dialogTitle"
                defaultMessage="Tag this photo with your bib number {icon}"
                values={{
                  icon: <HelpRoundedIcon className="display-sm-none" />,
                }}
              />
            </h1>
          </Tooltip>
        )}
        <div className="startnumber-search-container startnumber-search-container-darker ">
          {!isStartNumberInputDisabled && (
            <div
              className={classNames(
                'notification-start-number-label container-label ml-15 mr-15',
                {
                  error: startNumberIsInvalid,
                }
              )}
            >
              {startNumberIsInvalid ? (
                !notMainSearch && startNumber === props.startnumber ? (
                  <FormattedMessageWrappedInSpan
                    id="requestRecheckView.startNumberLabelRecheckErrorSameNumber"
                    defaultMessage="This photo is already tagged with this bib number"
                  />
                ) : (
                  <FormattedMessageWrappedInSpan
                    id="requestRecheckView.startNumberLabelRecheckError"
                    defaultMessage="Enter a valid bib number"
                  />
                )
              ) : (
                <FormattedMessageWrappedInSpan
                  id="requestRecheckView.startNumberLabel"
                  defaultMessage="Enter your bib number (alphanumeric)"
                />
              )}
            </div>
          )}
          {!isStartNumberInputDisabled && (
            <div className="startnumber-search-textfield input-edit-icon">
              <div>
                <input
                  autoComplete="off"
                  autoFocus={startNumber === ''}
                  disabled={isStartNumberInputDisabled}
                  onInput={handleStartnumberChanged}
                  onKeyPress={handleKeyPressed}
                  placeholder="0000"
                  value={startNumber || ''}
                />
              </div>
            </div>
          )}
        </div>
        {isStartNumberInputDisabled && (
          <h1>
            <FormattedMessageWrappedInSpan
              id="requestRecheckView.searchByFace.dialogTitle"
              defaultMessage="Tag this photo"
            />
          </h1>
        )}
        {isStartNumberInputDisabled && (
          <input
            autoComplete="off"
            autoFocus={startNumber === ''}
            disabled={isStartNumberInputDisabled}
            hidden={isStartNumberInputDisabled}
            onInput={handleStartnumberChanged}
            onKeyPress={handleKeyPressed}
            placeholder="0000"
            value={startNumber || ''}
          />
        )}
        <TextField
          autoFocus={startNumber !== ''}
          className={classNames('notification-input', 'email-search-textfield')}
          error={emailIsInvalid}
          label={intl.formatMessage(translations.emailTextFieldLabel)}
          onInput={handleEmailChanged}
          onKeyPress={handleKeyPressed}
          placeholder={intl.formatMessage(translations.emailTextFieldPlaceholder)}
          value={email || ''}
        />
        <div className="mt-20">
          <GDPRHint />
        </div>
      </div>

      <div className="notification-action">
        <button onClick={onCancel}>
          <FormattedMessageWrappedInSpan
            id="requestRecheckView.cancelButton"
            defaultMessage="Cancel"
          />
        </button>

        <button
          className={`thats-me-submit ${
            emailIsInvalid || startNumberIsInvalid ? 'disabled' : 'accept'
          }`}
          onClick={submitForm}
        >
          <FormattedMessageWrappedInSpan
            id="requestRecheckView.acceptButton"
            defaultMessage="Submit"
          />
        </button>
      </div>
    </div>
  );
};

RequestRecheckView.defaultProps = {
  isStartNumberInputDisabled: false,
};

RequestRecheckView.propTypes = {
  isStartNumberInputDisabled: PropTypes.bool,
  startnumber: PropTypes.string,
  email: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  intl: PropTypes.object,
  lastUserInputStartNumber: PropTypes.string,
  setLastUserInputStartNumber: PropTypes.func,
};

export default injectIntl(RequestRecheckView);
